<template>
  <div
    class="app-input-control"
    :class="{
      'app-input-control--password': innerType === 'password',
      'app-input-control--readonly': isReadonly,
      'app-input-control--is-disabled': isDisabled,
    }"
  >
    <label
      class="app-input-control__main"
      :class="{
        'app-input-control-main': isContent,
        'app-input-control__main--focused': focused,
      }"
    >
      <app-title-box
        v-if="title"
        class="app-input-control__title"
        :class="titleClass"
        size="small"
        :title="title"
        :isTooltip="!!tooltip"
        :tooltip="tooltip"
        :isRequired="isRequired"
      ></app-title-box>
      <input
        v-if="innerType === 'phone'"
        class="app-input-control__input"
        :class="inputClass"
        :type="inputTypeNative"
        :placeholder="placeholder | VMask('+7 (###) ###-##-##')"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        :value="innerValue"
        v-bind="$attrs"
        v-mask="maskPhone"
        :disabled="isDisabled"
        :readonly="isReadonly"
      />
      <vue-numeric
        v-else-if="innerType === 'numeric'"
        class="app-input-control__input"
        :class="inputClass"
        :min="min"
        :max="max"
        v-model="innerValue"
        :separator="separator"
        :precision="precision"
        :output-type="outputType"
        decimalSeparator=","
        emptyValue="0"
        @focus="handleFocus"
        @blur="handleBlur"
        :readOnly="isReadonly"
        :minus="isNumericMinus"
        readOnlyClass="app-input-control__input"
      ></vue-numeric>
      <input
        ref="mainInput"
        v-else
        class="app-input-control__input"
        :class="inputClass"
        :type="inputTypeNative"
        :placeholder="placeholder"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @input="handleInput"
        :value="innerValue"
        v-bind="$attrs"
        :disabled="isDisabled"
        :readonly="isReadonly"
        v-mask="mainMask"
      />
      <button
        v-if="passwordIcon && isIcon"
        @click="changePasswordVisible"
        type="button"
        class="app-input-control__password-btn"
        tabindex="-1"
      >
        <app-svg-icon
          isCurrentColor
          class="app-input-control__password-btn-icon"
          :name="passwordIconName"
        ></app-svg-icon>
      </button>
    </label>

    <template v-if="isContent">
      <div class="app-input-control__content">
        <slot name="content"> текст </slot>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'AppInputControl',
  components: {
    AppSvgIcon: () => import('@/components/common/AppSvgIcon'),
    AppTitleBox: () => import('@/components/common/AppTitleBox'),
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    mainMask: {
      type: String,
      default: '',
    },
    isRequired: {
      type: Boolean,
    },
    isNumericMinus: {
      type: Boolean,
    },
    outputType: {
      type: String,
      default: 'String',
    },
    separator: {
      type: String,
      default: 'space',
    },
    precision: {
      type: Number,
      default: 3,
    },
    max: {
      type: Number,
      default: 9007199254740991,
    },
    min: {
      type: Number,
      default: -9007199254740991,
    },
    type: {
      type: String,
      default: 'text',
    },
    placeholder: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Date],
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    isContent: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
    isIcon: {
      type: Boolean,
      default: true,
    },
    isNumber: {
      type: Boolean,
      default: false,
    },
    titleClass: {
      type: String,
      default: '',
    },
    inputClass: {
      type: String,
      default: '',
    },
    mask: {
      type: String,
      default: '',
    },
  },
  data: (vm) => ({
    innerType: vm.type,
    innerValue: '',
    passwordIcon: false,
    focused: false,
    maskPhone: '+7 (###) ###-##-##',
  }),
  computed: {
    /**
     * Отображение пароля
     */
    passwordVisible() {
      return this.innerType === 'password';
    },
    /**
     * Отображение иконки скрыть/показать пароль
     */
    passwordIconName() {
      return this.passwordVisible ? 'hide' : 'show';
    },
    /**
     * Отображение нативного типа инпута
     */
    inputTypeNative() {
      return this.innerType === 'phone' ? 'text' : this.innerType;
    },
  },
  watch: {
    value(value) {
      this.checkInnerValue(value);
    },
    innerValue(value) {
      if (this.innerType === 'numeric') {
        this.$emit('input', value);
      }
    },
    type(value) {
      this.innerType = value;
      this.passwordIcon = this.innerType === 'password';
    },
  },
  created() {
    this.checkInnerValue(this.value);
    this.passwordIcon = this.innerType === 'password';
  },
  methods: {
    /**
     * Проверка входящего значения, нужно для телефона
     * @param value
     */
    checkInnerValue(value) {
      if (
        this.innerType === 'phone' &&
        value.length > 9 &&
        !/\+/i.test(value)
      ) {
        this.maskPhone = '+# (###) ###-##-##';
        const newValue = value.split('').slice(1).join('');
        this.innerValue = `7${newValue}`;
      } else {
        this.maskPhone = '+7 (###) ###-##-##';
        this.innerValue = value;
      }
    },
    /**
     * Действие на событие input
     * @param event
     */
    handleInput(event) {
      if (this.type === 'num') {
        const reg = this.isNumber ? /[^0-9]/g : /[^0-9.,]/g;
        let num = event.target.value.replace(reg, '');
        if (this.mask) {
          num = this.$options.filters.VMask(num, this.mask);
        }
        this.$refs.mainInput.value = num;
        this.innerValue = num;
        this.$emit('input', num);
      } else {
        this.innerValue = event.target.value;
        this.$emit('input', event.target.value);
      }
    },
    /**
     * Действие на событие change
     * @param event
     */
    handleChange(event) {
      this.$emit('change', event);
    },
    /**
     * Действие на событие blur
     * @param event
     */
    handleBlur(event) {
      this.focused = false;
      this.$emit('blur', event);
    },
    /**
     * Действие на событие focus
     * @param event
     */
    handleFocus(event) {
      this.focused = true;
      this.$emit('focus', event);
    },
    /**
     * Смена типа инпута на password / text
     */
    changePasswordVisible() {
      if (this.innerType !== 'password') {
        this.innerType = 'password';
      } else {
        this.innerType = 'text';
      }
    },
    /**
     * Установка в фокус инпута (может использоваться из вне)
     */
    setFocus() {
      this.$refs.mainInput.focus();
    },
  },
};
</script>

<style lang="scss">
.app-input-control {
  $root: &;

  position: relative;
  width: 100%;

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 7px 12px;
    cursor: text;
    background: $white;
    border: 1px solid $gray-light;
    border-radius: 2px;
    transition: border $A;

    &:hover {
      border-color: $color-brand-green;
    }

    &--focused {
      border-color: $color-brand-green;
    }
  }

  &__content {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
  }

  &__title {
    margin-bottom: 8px;
  }

  &__input {
    padding: 0;
    font-size: rem(14);
    font-weight: 250;
    line-height: 22px;
    color: $black;
    letter-spacing: 0.02em;
    background-color: $color-transparent;
    border: 0;

    &::placeholder {
      color: $gray-dark;
    }

    &:disabled {
      background: $white;
    }

    @include autofill-background($color-white);
  }

  &__input[type='password'] {
    letter-spacing: 0.2em;
  }

  &__input-numeric-span {
    font-size: rem(14);
    font-weight: 250;
    line-height: 20px;
    color: $black;
    letter-spacing: 0.02em;
  }

  &__password-btn {
    @include button-reset;

    position: absolute;
    top: calc(50% - 7px);
    right: 20px;
    display: flex;
    width: 14px;
    height: 14px;
  }

  &__password-btn-icon {
    @include size(14px);

    fill: $gray;
    stroke: none !important;
    transition: fill $A;

    &:hover {
      fill: $accent;
    }
  }

  //
  &--password {
    #{$root}__input {
      margin-right: 40px;
    }
  }

  &--readonly {
    #{$root}__main {
      cursor: default;
      background: $gray-lighter;

      &:hover {
        border-color: $gray-light;
      }

      &--focused {
        border-color: $gray-light;
      }
    }
    #{$root}__input {
      cursor: default;
      background: $gray-lighter;

      @include autofill-background($gray-lighter);
    }
  }

  &--is-disabled {
    #{$root}__main {
      cursor: default;
      background: $gray-lighter;

      &:hover {
        border-color: $gray-light;
      }

      &--focused {
        border-color: $gray-light;
      }
    }
    #{$root}__input {
      cursor: default;
      background: $gray-lighter;

      @include autofill-background($gray-lighter);
    }
  }
}
</style>
